import { createContext, useContext } from "react";
import { ExternalLink, Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { defaultUrlTransform } from "react-markdown";
import localization from "../../../../../assets/localization";
import ChatService from "../../../../../front-services/ChatService";
import Analytics from "../../../../../utils/Analytics";
import Colors from "../../../../../utils/Colors";
import { useSliderState } from "../../slider/SliderState";
import { useBookingEnabled } from "../../slider/booking/BookingState";

/* ---------------------------------------------------------------------------
 * How a markdown link written by the assistant becomes UI.
 *
 * Until now EVERY link became a full-width "Prenota Ora" button. Measured over
 * a 90-day sample of real assistant messages that mislabelled 34 `mailto:`
 * links, 15 `wa.me` links, ~35 hotel/social/partner links, and 9 `tel:` links
 * whose href react-markdown had already emptied (see `widgetUrlTransform`) so
 * the button opened a blank about:blank tab. It also forced a block element in
 * the middle of a sentence, orphaning the text after it.
 *
 * Classification is now driven by the href alone — never by the surrounding
 * copy, never by a loose substring — so it cannot invent a booking intent that
 * is not there. Only a genuine booking/quote engine or a maps-directions link
 * gets the full-width call to action, and only when it is alone in its
 * paragraph; everything else stays inline.
 * ------------------------------------------------------------------------- */

export type LinkKind =
  | "email"
  | "phone"
  | "whatsapp"
  | "booking"
  | "directions"
  | "link";

/**
 * The ONLY hosts that earn a full-width booking/quote call to action.
 * These are the booking engines actually seen in production; add a host here
 * when a hotel is onboarded onto a new engine — nothing else needs to change.
 *
 * Matched on hostname (exact host, or a subdomain of it), never as a substring
 * of the whole URL: a blog post carrying `?utm_source=simplebooking.it` must
 * not turn into "Prenota Ora".
 */
const BOOKING_ENGINE_HOSTS = [
  "scidoo.com", // www.scidoo.com
  "verticalbooking.com", // reservations.verticalbooking.com
  "simplebooking.it", // www.simplebooking.it
  "pavilionshotels.com", // reservations.pavilionshotels.com
];

/**
 * Our own quote page — by far the most common booking link in production.
 * It lives on the same Firebase host that serves the widget bundle itself, so
 * unlike the engines above this one is only a booking link on a specific path.
 */
const QUOTE_PAGE_HOST = "widgets-plutonios-prod.web.app";
const QUOTE_PAGE_PATH = "/slope.html";

/** WhatsApp deep links. `wa.me` and `api.whatsapp.com` are what assistants emit. */
const WHATSAPP_HOSTS = [
  "wa.me",
  "api.whatsapp.com",
  "web.whatsapp.com",
  "chat.whatsapp.com",
];

/** Google Maps turn-by-turn links, e.g. /maps/dir/?api=1&destination=… */
const MAPS_DIRECTIONS_PATH = "/maps/dir";

/** `tel:`/`sms:` are dialable, inert schemes — see `widgetUrlTransform`. */
const DIALABLE_SCHEME = /^(?:tel|sms):/i;
const MAILTO_SCHEME = /^mailto:/i;

/** Six-digit hex is all `Colors.lightenHexColor` can safely parse. */
const HEX_COLOR = /^#[0-9a-f]{6}$/i;

const ANALYTICS_CTA: Record<LinkKind, string> = {
  booking: "book_now",
  directions: "directions",
  email: "email",
  phone: "phone",
  whatsapp: "whatsapp",
  link: "link",
};

const parseUrl = (value: string): URL | null => {
  try {
    // No base on purpose: a relative or malformed href must never be able to
    // borrow our origin and classify as a booking link.
    return new URL(value);
  } catch {
    return null;
  }
};

const matchesHost = (hostname: string, domain: string) =>
  hostname === domain || hostname.endsWith(`.${domain}`);

/**
 * Deterministic href -> kind. `href` is typed `any` because react-markdown can
 * hand us `undefined` (and did, for every `tel:` link), so everything below
 * has to survive that.
 */
export const classifyLink = (href: any): LinkKind => {
  const url = typeof href === "string" ? href.trim() : "";
  if (!url) return "link";

  if (MAILTO_SCHEME.test(url)) return "email";
  if (DIALABLE_SCHEME.test(url)) return "phone";

  const parsed = parseUrl(url);
  if (!parsed) return "link";

  const host = parsed.hostname.toLowerCase();
  const path = parsed.pathname.toLowerCase();

  if (WHATSAPP_HOSTS.some((domain) => matchesHost(host, domain))) {
    return "whatsapp";
  }
  if (BOOKING_ENGINE_HOSTS.some((domain) => matchesHost(host, domain))) {
    return "booking";
  }
  if (matchesHost(host, QUOTE_PAGE_HOST) && path.startsWith(QUOTE_PAGE_PATH)) {
    return "booking";
  }
  if (path.includes(MAPS_DIRECTIONS_PATH)) return "directions";

  return "link";
};

/**
 * react-markdown sanitises every href through `defaultUrlTransform`, whose
 * allowlist is `/^(https?|ircs?|mailto|xmpp)$/i`. `tel:` is not in it, so
 * `[+39 0331 327511](tel:+390331327511)` arrived here with href === "" and the
 * old renderer happily called `window.open("")`.
 *
 * This widens the allowlist by exactly two inert, dialable schemes and hands
 * EVERYTHING else — `javascript:`, `data:`, `vbscript:`, relative paths — to
 * the untouched default, so the sanitisation is not weakened.
 */
export const widgetUrlTransform = (value: string): string =>
  DIALABLE_SCHEME.test(value) ? value : defaultUrlTransform(value);

/* ---------------------------------------------------------------------------
 * "Is this link alone in its paragraph?"
 *
 * Only a link that is the entire paragraph may become a block-level button —
 * that is what stops `…via email a [x](mailto:x) oppure al telefono…` from
 * being sliced in half. `node.position` offsets cannot answer this (they say
 * where the link is in the SOURCE, not whether the paragraph holds anything
 * else), so the paragraph itself decides: a custom `p` renderer inspects its
 * own hast children and publishes the verdict on a context the `a` renderer
 * reads. Default `false`, so a link in any container we do not override
 * (heading, table cell, tight list item) stays inline.
 * ------------------------------------------------------------------------- */
const IsolatedLinkContext = createContext(false);

/**
 * Lists get their own flag: a tight list item has no paragraph wrapper at all,
 * and a loose one does — without this a `- [book](scidoo)` bullet would grow a
 * full-width button inside the `<li>`.
 */
const InListContext = createContext(false);

const isLoneLinkParagraph = (node: any): boolean => {
  const children = (node?.children ?? []).filter(
    (child: any) =>
      !(child?.type === "text" && String(child.value ?? "").trim() === "")
  );

  return (
    children.length === 1 &&
    children[0]?.type === "element" &&
    children[0]?.tagName === "a"
  );
};

export const MessageParagraph = ({ node, children, ...props }: any) => {
  const inList = useContext(InListContext);

  return (
    <IsolatedLinkContext.Provider value={!inList && isLoneLinkParagraph(node)}>
      <p {...props}>{children}</p>
    </IsolatedLinkContext.Provider>
  );
};

const listRenderer = (Tag: "ul" | "ol") => {
  const MessageList = ({ node, children, ...props }: any) => (
    <InListContext.Provider value={true}>
      <Tag {...props}>{children}</Tag>
    </InListContext.Provider>
  );

  return MessageList;
};

export const MessageUnorderedList = listRenderer("ul");
export const MessageOrderedList = listRenderer("ol");

/* ------------------------------------------------------------------------- */

/** Flattens react-markdown children down to the text the model actually wrote. */
const toPlainText = (node: any): string => {
  if (node === null || node === undefined || typeof node === "boolean") {
    return "";
  }
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(toPlainText).join("");
  if (node.props) return toPlainText(node.props.children);
  return "";
};

/** `mailto:info@x.it?subject=…` -> `info@x.it`; `tel:+3903…` -> `+3903…`. */
const schemePayload = (url: string, scheme: RegExp): string => {
  const parsed = parseUrl(url);
  const raw = parsed ? parsed.pathname : url.replace(scheme, "").split("?")[0];

  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
};

/**
 * The chip shows what will actually be dialled/mailed — never the model's
 * prose, which could say "chiamaci" over a completely different number. The
 * one exception is pure formatting: `[+39 0331 327511](tel:+390331327511)` is
 * the same value spaced out, so the readable spelling wins.
 */
const readableTarget = (target: string, text: string): string =>
  text && text.replace(/[\s().\-/]/g, "") === target.replace(/[\s().\-/]/g, "")
    ? text
    : target;

const hostLabel = (url: string): string =>
  parseUrl(url)?.hostname.replace(/^www\./i, "") ?? url;

/**
 * Brand colour for link surfaces, guarded: a missing or short hex must never
 * reach `lightenHexColor` and come back as `#NaNNaNNaN`.
 *
 * The full-width CTA keeps the historical `lighten(20)`. Chips use the brand
 * colour undiluted — their text is smaller, and lighten(20) on white sits
 * under the WCAG AA contrast floor for several tenant palettes.
 */
const accentColor = (style: any, lighten: number): string => {
  const color = typeof style?.color === "string" ? style.color : "";

  if (!HEX_COLOR.test(color)) return "var(--primary-color)";

  return lighten > 0 ? Colors.lightenHexColor(color, lighten) : color;
};

const CustomLinkRenderer = ({
  href,
  assistantId,
  language,
  style,
  children,
}: any) => {
  const isolated = useContext(IsolatedLinkContext);
  // Before the early return below: hooks cannot be called conditionally.
  const bookingEnabled = useBookingEnabled();

  const url = typeof href === "string" ? href.trim() : "";
  const kind = classifyLink(url);
  const strings = localization.get(language);
  const text = toPlainText(children).trim();

  // Nothing to open: render the words so the sentence still reads instead of
  // shipping a dead control.
  if (!url) return <>{children}</>;

  const track = () =>
    Analytics.track("link_click", {
      url,
      cta: ANALYTICS_CTA[kind],
      source: "message",
    });

  const chip = (
    icon: JSX.Element,
    label: string,
    ariaLabel: string,
    newTab: boolean
  ) => (
    <a
      className="msg-link-chip"
      href={url}
      aria-label={ariaLabel}
      style={{ color: accentColor(style, 0) }}
      onClick={track}
      {...(newTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    >
      {icon}
      <span>{label}</span>
    </a>
  );

  if (kind === "email") {
    const address = schemePayload(url, MAILTO_SCHEME);
    return chip(
      <Mail size={14} strokeWidth={2.4} aria-hidden="true" />,
      readableTarget(address, text),
      `${strings.send_email}: ${address}`,
      false
    );
  }

  if (kind === "phone") {
    const number = schemePayload(url, DIALABLE_SCHEME);
    return chip(
      <Phone size={14} strokeWidth={2.4} aria-hidden="true" />,
      readableTarget(number, text),
      `${strings.call_now}: ${number}`,
      false
    );
  }

  if (kind === "whatsapp") {
    // The model usually writes the number or a short "scrivici qui" — keep it,
    // the icon already says WhatsApp. Only fall back to the localized label.
    const label = text || strings.whatsapp_chat;
    return chip(
      <MessageCircle size={14} strokeWidth={2.4} aria-hidden="true" />,
      label,
      `${strings.whatsapp_chat}: ${label}`,
      true
    );
  }

  if (kind === "booking" || kind === "directions") {
    const label =
      kind === "directions"
        ? strings.map_directions
        : ChatService.isHM(assistantId)
        ? strings.request_quote
        : strings.book_now;

    // With booking on, this link must not be the one exit that still leaves for
    // the engine. It carries no room or rate — only a search URL — so it can't
    // start a specific draft; what it can do is surface the quote the assistant
    // just showed, and let the guest pick there. If no quote is in the stack
    // (an old message, a cleared session) we leave the link alone rather than
    // hand back a dead control.
    if (kind === "booking" && bookingEnabled) {
      const openQuote = (event: React.MouseEvent) => {
        const slider = useSliderState.getState();
        const quote = (slider.templates ?? []).find(
          (t: any) => t?.type === "quote"
        );
        if (!quote) return; // let the anchor navigate as before
        event.preventDefault();
        Analytics.track("link_click", {
          url,
          cta: ANALYTICS_CTA[kind],
          source: "message",
          opened_in_widget: true,
        });
        slider.setCurrentTemplate(quote, 0);
        slider.justShow();
        slider.setExpanded(true);
      };

      return (
        <a
          className={isolated ? "shortcut-button msg-link-cta" : "msg-link-chip"}
          href={url}
          aria-label={label}
          style={{ color: accentColor(style, isolated ? 20 : 0) }}
          onClick={openQuote}
          target="_blank"
          rel="noopener noreferrer"
        >
          {!isolated && (
            <ExternalLink size={14} strokeWidth={2.4} aria-hidden="true" />
          )}
          {isolated ? <b>{label}</b> : <span>{label}</span>}
        </a>
      );
    }

    // Mid-sentence, the call to action becomes a chip: same wording, no block
    // element cutting the paragraph in two.
    if (!isolated) {
      return chip(
        kind === "directions" ? (
          <MapPin size={14} strokeWidth={2.4} aria-hidden="true" />
        ) : (
          <ExternalLink size={14} strokeWidth={2.4} aria-hidden="true" />
        ),
        label,
        label,
        true
      );
    }

    return (
      <a
        className="shortcut-button msg-link-cta"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: accentColor(style, 20) }}
        onClick={track}
      >
        <b>{label}</b>
      </a>
    );
  }

  return (
    <a
      className="msg-link-inline"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={text ? undefined : `${strings.open_link}: ${hostLabel(url)}`}
      onClick={track}
    >
      {text || hostLabel(url)}
      <ExternalLink size={12} strokeWidth={2.6} aria-hidden="true" />
    </a>
  );
};

/**
 * Bot messages: the full classification above, plus the paragraph/list
 * renderers it needs to know whether a link stands alone.
 */
export const botMarkdownComponents = ({
  assistantId,
  language,
  style,
}: {
  assistantId: string;
  language: string;
  style: any;
}) => ({
  a: (props: any) => (
    <CustomLinkRenderer
      {...props}
      assistantId={assistantId}
      language={language}
      style={style}
    />
  ),
  p: MessageParagraph,
  ul: MessageUnorderedList,
  ol: MessageOrderedList,
});

/**
 * Guest messages: a pasted URL is just a URL. No classification, no CTA — a
 * guest must never be handed a "Prenota Ora" button of their own making.
 */
export const GuestLinkRenderer = ({ href, children }: any) => {
  const url = typeof href === "string" ? href.trim() : "";
  if (!url) return <>{children}</>;

  return (
    <a
      className="msg-link-inline"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  );
};

export default CustomLinkRenderer;
